import React, { useState, useEffect, useRef } from "react";
import { Row } from "react-bootstrap";
import { FaShoppingBag } from "react-icons/fa";
import { HiSparkles } from "react-icons/hi2";
// Import external CSS (includes animations)
import './ProductGridPage.css';
import { elakayAutoText, getElakayLanguageContext } from "./dataElakay";
import { CARD_MODE_STORAGE_KEY, CARD_MODE_EVENT, getCardColumns } from "../../utils/cardDisplaySettings";
import { buildProductPagination } from "../../utils/productPagination";
import InfiniteScrollLoader from "./InfiniteScrollLoader";
import { mergeUniqueById, resolveInfiniteScroll } from "../../utils/infiniteScrollSettings";
import { ProductGridSkeleton } from "./ProductGridPage";


let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
// Current language source: the app-wide one (always up to date after a language switch), else
// the infoSource this module last rendered with.
const __elakayLanguageSource = () => getElakayLanguageContext?.() || __elakayInfoSource;
const __elakayText = (key, fallback) =>
  __elakayLanguageSource()?.laguangeElakay?.("frontElakayHardcodedText", __elakayLanguageSource().languageAPI)?.["productGridShop"]?.[key] || fallback;

// ============================================
// HELPER FUNCTIONS
// ============================================
const getRandomColor = () => {
  const colors = [
    'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
    'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
    'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
    'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
    'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
    'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)'
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

// ============================================
// RESULT PAGINATION COMPONENT
// ============================================
const ResultPagination = ({
  prodCat = "",
  skip = 0,
  limit = 12,
  size = 0,
  siseProduct = 0,
  infoSource,
  // Customization
  style = "minimal", // transparent, gradient, glass, dark, minimal, success, warm, cool, rose, indigo, slate, custom
  customBackground = null,
  icon = null,
  showIcon = true,
  showBadge = true,
  showPageInfo = true,
  showTitle = true,
  animated = false, // Enable/disable animation
  className = "",
  textColor = null,
  textSize = null
}) => {
  __setElakayInfoSource(infoSource);
  const pagination = buildProductPagination({ skip, limit, size, totalProducts: siseProduct });
  const { start, end, currentPage, totalPages } = pagination;
  const ofText = infoSource?.language?.(infoSource, "label", "of") || "of";
  const itemsText = infoSource?.laguangeElakay?.('label', infoSource.languageAPI)?.items || "items";

  const styleClass = customBackground ? 'style-custom' : `style-${style}`;
  const animatedClass = animated ? 'animated' : '';
  const customStyle = {
    ...(customBackground ? { background: customBackground } : {}),
    ...(textColor ? { color: textColor } : {}),
    ...(textSize ? { fontSize: textSize } : {}),
  };

  return (
    <div 
      className={`pgp-result-bar ${styleClass} ${animatedClass} ${className}`}
      style={customStyle}
    >
      <div className="pgp-result-left">
        {showIcon && (
          <div className="pgp-result-icon">
            {icon || <FaShoppingBag />}
          </div>
        )}
        <div className="pgp-result-content">
          {showTitle ? <h5>
            {prodCat || elakayAutoText("h_fe0a091fdbfb3e", "Products")}
            {showBadge && (
              <span className="pgp-result-badge">
                <HiSparkles size={14} />
                {siseProduct} {itemsText}
              </span>
            )}
          </h5> : null}
          <p>
            {start} - {end} {ofText} {siseProduct} {itemsText}
          </p>
        </div>
      </div>
      
      {showPageInfo && (
        <div className="pgp-result-right">
          <div className="pgp-page-info">{__elakayText("t002", "Page")}{currentPage} / {totalPages}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// PRODUCT GRID COMPONENT
// ============================================
const ProductGrid = ({
  productsByArrival = [],
  loading = false,
  currentShopId = null,
  Card,
  laguangeElakay,
  infoSource,
  promoMaker,
  btnShopPromo,
  refShopName,
  elakayEmptyBox,
  cardMode = 'portrait',
  // Customization
  style = "minimal", // gradient, glass, dark, minimal, warm, cool, rose, success, indigo, slate, transparent, custom
  customBackground = null,
  columns = { xs: 2, sm: 2, md: 3, lg: 4 },
  emptyCount = 12,
  animated = false, // Enable/disable animation
  animateCards = false, // Enable/disable card appear animation
  className = "",
  facet = null,
   edit = false,
  textColor = null,
  textSize = null,
}) => {
  const styleClass = customBackground ? 'style-custom' : `style-${style}`;
  const animatedClass = animated ? 'animated' : '';
  const customStyle = {
    ...(customBackground ? { background: customBackground } : {}),
    ...(textColor ? { color: textColor } : {}),
    ...(textSize ? { fontSize: textSize } : {}),
  };

  // Render product cards
  const renderCards = () => {
    const cards = [];

    // Render actual products
    productsByArrival?.forEach((product, i) => {
      cards.push(
        <div 
          className={`col pgp-product-col ${animateCards ? 'animate-appear' : ''}`} 
          key={product._id || i}
        >
          <Card
            laguangeElakay={laguangeElakay}
            infoSource={infoSource}
            product={product}
            currentShopId={currentShopId}
            classCard0="pgp-card-shell"
            showShopV={false}
            promoMaker={promoMaker}
            btnShopPromo={btnShopPromo}
            refShopName={refShopName}
            edit={edit}
            displayMode={cardMode}
            isGridPage={true}
          />
        </div>
      );
    });

    // Add empty cards if no products
    if (loading && productsByArrival?.length === 0) {
      const fillers = Math.max(0, emptyCount - (productsByArrival?.length || 0));
      for (let f = 0; f < fillers; f++) {
        cards.push(
          <div className="col pgp-product-col" key={`filler-${f}`}>
            <div className="pgp-empty-card">
              <div 
                className="pgp-empty-card-image" 
                style={{ background: getRandomColor() }}
              >
                {elakayEmptyBox && (
                  <img
                    src={elakayEmptyBox}
                    alt={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).productGridShop.t001}
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                )}
              </div>
              <div className="pgp-empty-card-body">
                <div className="pgp-skeleton title"></div>
                <div className="pgp-skeleton subtitle"></div>
                <div className="pgp-skeleton price"></div>
              </div>
            </div>
          </div>
        );
      }
    }

    return cards;
  };

  return (
    <div 
      className={`pgp-grid-wrapper ${styleClass} ${animatedClass} ${className}`}
      style={customStyle}
    >
      <Row 
        className="pgp-product-row" 
        xs={columns.xs} 
        sm={columns.sm} 
        md={columns.md} 
        lg={columns.lg}
      >
        {renderCards()}
      </Row>
    </div>
  );
};

// ============================================
// MAIN COMPONENT - ProductGridShop
// ============================================
const ProductGridShop = ({
  // Required props

  productsByArrival = [],
  loading = false,
  currentShopId = null,
  skip = 0,
  limit = 12,
  siseProduct = 0,
  size,
  
  // Category/Shop
  prodCat = "",
  
  // Card component props
  Card,
  laguangeElakay,
  infoSource,
  promoMaker = false,
  btnShopPromo,
  refShopName,
  
  // Pagination
  ButtonPagination,
  loadMore,
  loadMore0,
  
  // Empty state
  elakayEmptyBox,
  
  // ===== CUSTOMIZATION =====
  
  // Result bar customization
  // Valeurs par défaut lues depuis AnimationSettings
  // (infoSource.allSettings.productGrids.productGridShop) si non fournies
  // explicitement par l'appelant — voir effective* ci-dessous.
  resultStyle = "minimal", // transparent, gradient, glass, dark, minimal, success, warm, cool, rose, indigo, slate, custom
  resultBackground, // custom CSS background
  resultIcon = null,
  showResultIcon = true,
  showResultBadge = true,
  showResultPageInfo = true,
  resultAnimated, // Enable result bar animation

  // Grid wrapper customization
  gridStyle = "minimal", // gradient, glass, dark, minimal, warm, cool, rose, success, indigo, slate, transparent, custom
  gridBackground, // custom CSS background
  gridAnimated, // Enable grid background animation
  animateCards, // Enable card appear animation

  // Grid columns
  columns,

  // Container style
  containerStyle = {},
  containerClassName = "mt-3 mb-3",

  // Empty cards count
  emptyCount = 12,
  divCatProduct,
   edit,
   page = 'SHOP_USER',
   sectionId = 'shopProductGrid',
   // Shop Builder "Product Grid" block: 'inherit' (platform setting, the
   // historical behaviour), 'pagination' or 'infinite'. In 'infinite' the
   // caller already accumulates the products and loads each batch through
   // onLoadMoreAsync() -> { ok, received, error }.
   loadingMode = 'inherit',
   onLoadMoreAsync = null,
   appending = false,
   forcedCardMode = null,
}) => {

  const platformInfinite = resolveInfiniteScroll(infoSource?.allSettings, page, sectionId);
  const externalInfinite = loadingMode === 'infinite' && typeof onLoadMoreAsync === 'function';
  const infiniteConfig = loadingMode === 'pagination'
    ? { ...platformInfinite, enabled: false }
    : externalInfinite ? { ...platformInfinite, enabled: true } : platformInfinite;
  const [accumulatedProducts, setAccumulatedProducts] = useState(productsByArrival);
  const [loadError, setLoadError] = useState('');
  const [endReached, setEndReached] = useState(false);
  const pendingRef = useRef(false);
  const shopRef = useRef(currentShopId);
  useEffect(() => {
    const shopChanged = shopRef.current !== currentShopId;
    shopRef.current = currentShopId;
    if (externalInfinite) return;
    if (!infiniteConfig.enabled || skip === 0 || shopChanged) setAccumulatedProducts(productsByArrival || []);
    else setAccumulatedProducts((current) => mergeUniqueById(current, productsByArrival || []));
    pendingRef.current = false;
  }, [productsByArrival, skip, infiniteConfig.enabled, currentShopId, externalInfinite]);
  // A new first product / total = a new list (category, search, sort).
  const firstProductId = productsByArrival?.[0]?._id;
  useEffect(() => {
    setEndReached(false);
    setLoadError('');
  }, [firstProductId, siseProduct, currentShopId]);
  const visibleProducts = externalInfinite ? (productsByArrival || []) : infiniteConfig.enabled ? accumulatedProducts : productsByArrival;
  const requestNext = async () => {
    if (pendingRef.current || loading || appending) return;
    pendingRef.current = true;
    if (externalInfinite) {
      setLoadError('');
      const result = await onLoadMoreAsync();
      pendingRef.current = false;
      if (result?.ok && Number(result.received) === 0) setEndReached(true);
      else if (result && !result.ok && !result.busy && !result.stale) setLoadError(elakayAutoText('h_spg_load_error', 'The next products could not be loaded.'));
      return;
    }
    loadMore?.();
  };
  const externalHasMore = externalInfinite && !endReached && visibleProducts.length < Number(siseProduct || 0);

  // Card Display Mode (Portrait/Landscape) — Grid page. Préférence globale
  // (localStorage), poussée en direct par SideDrawer1.js via un événement
  // custom (voir utils/cardDisplaySettings.js).
  const [cardMode, setCardMode] = useState(
    () => (typeof window !== 'undefined' && window.localStorage.getItem(CARD_MODE_STORAGE_KEY)) || 'portrait'
  );
  useEffect(() => {
    const updateMode = (e) => {
      setCardMode(e.detail?.mode || window.localStorage.getItem(CARD_MODE_STORAGE_KEY) || 'portrait');
    };
    window.addEventListener(CARD_MODE_EVENT, updateMode);
    return () => window.removeEventListener(CARD_MODE_EVENT, updateMode);
  }, []);

  // Colonnes par appareil — dépendent du mode actif (portrait/landscape),
  // une prop explicite passée par l'appelant reste prioritaire.
  const effectiveCardMode = forcedCardMode || cardMode;
  const effectiveColumns = getCardColumns(effectiveCardMode, infoSource?.allSettings?.cardDisplaySettings, columns);

  const gridSettings = infoSource?.allSettings?.productGrids?.productGridShop || {};
  const effectiveResultBackground = resultBackground ?? gridSettings.resultBackground ?? null;
  const effectiveResultAnimated   = resultAnimated   ?? gridSettings.resultAnimated   ?? false;
  const effectiveGridBackground   = gridBackground   ?? gridSettings.gridBackground   ?? null;
  const effectiveGridAnimated     = gridAnimated     ?? gridSettings.gridAnimated     ?? false;
  const effectiveAnimateCards     = animateCards     ?? gridSettings.animateCards     ?? false;


  return (
    <div 
      ref={divCatProduct} 
      className={containerClassName}
      style={containerStyle}
    >
      {/* Result Pagination Bar */}
      <ResultPagination
        prodCat={infiniteConfig.titleMode === 'custom' ? infiniteConfig.customTitle : prodCat}
        showTitle={infiniteConfig.titleMode !== 'hidden'}
        skip={skip}
        limit={limit}
        size={visibleProducts.length}
        siseProduct={siseProduct}
        infoSource={infoSource}
        style={resultStyle}
        customBackground={effectiveResultBackground}
        icon={resultIcon}
        showIcon={showResultIcon}
        showBadge={showResultBadge}
        showPageInfo={showResultPageInfo}
        animated={effectiveResultAnimated}
        textColor={gridSettings.resultTextColor}
        textSize={gridSettings.resultTextSize}
      />

      {!loading && !visibleProducts.length && <p role="status">{elakayAutoText('h_msl_empty', 'No Products Available')}</p>}
      {/* Product Grid */}
      <ProductGrid
        productsByArrival={visibleProducts}
        loading={loading}
        currentShopId={currentShopId}
        Card={Card}
        laguangeElakay={laguangeElakay}
        infoSource={infoSource}
        promoMaker={promoMaker}
        btnShopPromo={btnShopPromo}
        refShopName={refShopName}
        elakayEmptyBox={elakayEmptyBox}
        style={gridStyle}
        customBackground={effectiveGridBackground}
        columns={effectiveColumns}
        emptyCount={emptyCount}
        animated={effectiveGridAnimated}
        animateCards={effectiveAnimateCards}
         edit={edit}
        textColor={gridSettings.gridTextColor}
        textSize={gridSettings.gridTextSize}
        cardMode={effectiveCardMode}
      />

      <InfiniteScrollLoader
        enabled={infiniteConfig.enabled}
        hasMore={externalInfinite ? externalHasMore : visibleProducts.length < siseProduct && Number(size ?? productsByArrival.length) > 0}
        // The store's own (first page / filter) loading counts too: when it
        // ends the observer is re-armed, so a sentinel already in view loads.
        loading={externalInfinite ? (appending || loading) : loading && visibleProducts.length > 0}
        error={externalInfinite ? loadError : undefined}
        onRetry={requestNext}
        onLoadMore={requestNext}
        skeleton={<ProductGridSkeleton count={Math.min(limit, 4)} columns={effectiveColumns} />}
      />
      {externalInfinite && externalHasMore && !appending && !loading && !loadError && (
        // Keyboard / no-IntersectionObserver fallback for the same request.
        <div className="pgp-infinite-more">
          <button type="button" className="pgp-infinite-more__button" onClick={requestNext}>
            {elakayAutoText('h_spg_load_more', 'Show more products')}
          </button>
        </div>
      )}
      {externalInfinite && !externalHasMore && !loading && visibleProducts.length > 0 && (
        <p className="pgp-infinite-end" role="status">
          {elakayAutoText('h_spg_end', 'You have seen all {count} products.', { count: visibleProducts.length })}
        </p>
      )}

      {/* Pagination - Your existing ButtonPagination */}
      {ButtonPagination && !infiniteConfig.enabled && (
        <div 
        // className="pgp-pagination-wrapper"
        >
          <ButtonPagination
            infoSource={infoSource}
            size={size}
            skip={skip}
            limit={limit}
            siseProduct={siseProduct}
            loadMore={loadMore}
            loadMore0={loadMore0}
          />
        </div>
      )}
    </div>
  );
};

// Export components separately for flexible usage
export { ResultPagination, ProductGrid };
export default ProductGridShop;
